import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Descriptions, Form, Grid, InputNumber, Modal, Typography } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { updateTransportationTicketAfterConfirm } from 'src/apis/hr_ticket/transportationTicket.api';
import { getFuelConsumption } from 'src/apis/master_data/fuelConsumption.api';
import { BranchSelect } from 'src/components/v1/Select';
import { OpenEditTransportationType } from 'src/components/v2/Button/BtnHrHandle';
import { UTCNoShiftDatePicker } from 'src/components/v2/DatePicker';
import {
  DistanceSelect,
  DriverSelect,
  TransportationTypeSelect,
  VehicleSelect,
  VehicleTypeSelect
} from 'src/components/v2/Select';
import { HRTicketStatusTag } from 'src/components/v2/Tag/TicketStatusTag/TicketStatusTag';
import { TransportationTypeEn } from 'src/constants/transportationType.enum';
import { TransportationTicketEdit } from 'src/types/hr_ticket/transportationTicket.type';
import { showNotification } from 'src/utils/notification';
import { formatDate, formatToVND } from 'src/utils/utils';
import './editTransportationTicket.scss';

const { Text } = Typography;

interface Props {
  open: OpenEditTransportationType;
  setOpen: React.Dispatch<React.SetStateAction<OpenEditTransportationType>>;
}

type FormStateType = Omit<TransportationTicketEdit, 'transportationTicketAttachments'>;

const initialFormState: FormStateType = {
  departureId: undefined,
  departureAddress: undefined,
  destinationId: undefined,
  destinationAddress: undefined,
  distance: undefined,
  departureDate: undefined,
  arriveDate: undefined,
  expectedReturnDate: undefined,
  transportationTypeId: undefined,
  vehicleTypeId: undefined,
  vehicleId: undefined,
  driverId: undefined,
  note: undefined,
  cost: undefined,
  estimatedCost: undefined
};

export default function EditTransportationTicket({ open, setOpen }: Props) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { transportationTicket } = open;
  const [formState, setFormState] = useState<FormStateType>(initialFormState);
  const { id } = useParams();
  const queryClient = useQueryClient();

  const { useBreakpoint } = Grid;
  const breakpoints = useBreakpoint();
  const colspan = breakpoints.xxl ? 2 : 1;

  useEffect(() => {
    if (transportationTicket) {
      if (transportationTicket?.transportationTicketVerify) {
        setFormState(transportationTicket?.transportationTicketVerify);
      } else {
        setFormState(transportationTicket as FormStateType);
      }
    }
  }, [transportationTicket]);

  const fuelConsumptionQuery = useQuery({
    queryKey: ['fuelConsumption', formState.vehicleTypeId],
    queryFn: () => getFuelConsumption(formState?.vehicleTypeId),
    enabled: !!formState.vehicleTypeId
  });

  const updateTransportationTicketMutation = useMutation({
    mutationFn: (body: TransportationTicketEdit) => updateTransportationTicketAfterConfirm(Number(id), body)
  });

  useEffect(() => {
    const totalInKm = fuelConsumptionQuery?.data?.data.totalInKm ?? 0;
    const unitPrice = fuelConsumptionQuery?.data?.data.fuelType?.fuelUnitPrice?.unitPrice ?? 0;

    if (fuelConsumptionQuery?.data?.data?.fuelType?.fuelUnitPrice) {
      if (
        formState.transportationTypeId === TransportationTypeEn.selfSufficient ||
        formState.transportationTypeId === TransportationTypeEn.shuttle
      ) {
        const estimatedCode = (formState?.totalKm ?? 0 / totalInKm) * unitPrice;
        setFormState((prev) => ({ ...prev, estimatedCost: Math.round(estimatedCode ?? 0) }));
      }
    } else {
      setFormState((prev) => ({ ...prev, estimatedCost: 0 }));
    }
  }, [formState.totalKm, fuelConsumptionQuery.data?.data, formState.transportationTypeId]);

  const handleChangeDistance = (value: string) => {
    const data = value ? JSON.parse(value) : undefined;

    setFormState((prev) => ({
      ...prev,
      departureId: data?.departureId ?? undefined,
      destinationId: data?.destinationId ?? undefined,
      distance: data?.distance ?? undefined
    }));
  };

  const handleChangeVehicleType = (value: string) => {
    setFormState((prev) => ({
      ...prev,
      vehicleTypeId: value ? Number(value) : undefined
    }));
  };

  const handleSubmit = () => {
    form
      .validateFields()
      .then(() => {
        const transportationTicket = {
          departureId: formState.departureId,
          destinationId: formState.destinationId,
          distance: formState.distance,
          departureDate: formState.departureDate,
          arriveDate: formState.arriveDate,
          expectedReturnDate: formState.expectedReturnDate,
          transportationTypeId: formState.transportationTypeId,
          vehicleTypeId: formState.vehicleTypeId,
          vehicleId: formState.vehicleId,
          driverId: formState.driverId,
          note: formState.note,
          estimatedCost: formState.estimatedCost,
          transportationTicketAttachments: open.transportationTicket?.transportationTicketAttachments?.map((file) => ({
            path: file.path || file.filePath,
            name: file.name || file.originalName,
            thumbnailPath: '',
            fileType: file.fileType || file.contentType,
            size: 0
          }))
        };

        updateTransportationTicketMutation.mutate(transportationTicket, {
          onSuccess: () => {
            showNotification(`Cập nhật thông tin vận chuyển thành công.`, 'success');

            setOpen({ isOpen: false, transportationTicket: undefined });
            form.resetFields();
            setFormState(initialFormState);
            queryClient.invalidateQueries({
              queryKey: ['transportationTicket']
            });
          }
        });
      })
      .catch(() => {});
  };

  const handleCancel = () => {
    setOpen({ isOpen: false, transportationTicket: undefined });
    form.resetFields();
    setFormState(initialFormState);
  };

  // const renderStatus = (item: ITransportationTicket | undefined) => {
  //   if (item?.ticketStatusId === TicketStatus.ApprovedRequest) {
  //     if (basePath?.includes(urlPath.hrConfirmTransportationTicket.location)) {
  //       switch (item?.confirmedType) {
  //         case ConfirmTypeEn.Confirmed:
  //           return <Tag color='var(--color-approved-request)'>Đã xác nhận</Tag>;
  //         case ConfirmTypeEn.Reject:
  //           return <Tag color='var(--color-rejected)'>Đã từ chối</Tag>;
  //         case ConfirmTypeEn.WaitingConfirm:
  //           return <Tag color='var(--color-new)'>Chờ xác nhận</Tag>;
  //         default:
  //           break;
  //       }
  //     } else if (basePath?.includes(urlPath.transportationTicket.location)) {
  //       switch (item?.confirmedType) {
  //         case ConfirmTypeEn.Confirmed:
  //           return <Tag color='var(--color-approved-request)'>Hoàn thành</Tag>;
  //         case ConfirmTypeEn.Reject:
  //           return <Tag color='var(--color-rejected)'>Đã từ chối</Tag>;
  //         case ConfirmTypeEn.WaitingConfirm:
  //           return <Tag color='var(--color-new)'>Chờ xác nhận</Tag>;

  //         default:
  //           break;
  //       }
  //     }
  //   } else if (item?.ticketStatusId === TicketStatus.Reject) {
  //     return <Tag color='var(--color-rejected)'>{item?.ticketStatus.name}</Tag>;
  //   } else if (item?.ticketStatusId === TicketStatus.Canceled) {
  //     return <Tag color='var(--color-canceled)'>{item?.ticketStatus.name}</Tag>;
  //   } else return <Tag color='var(--color-new)'>{item?.ticketStatus.name}</Tag>;
  // };

  return (
    <Modal
      title='Cập nhật thông tin vận chuyển'
      centered
      maskClosable={false}
      className='edit-transportation-modal'
      open={open.isOpen}
      onOk={handleSubmit}
      onCancel={handleCancel}
      styles={{ header: { marginBlockEnd: 16 } }}
      footer={[
        <Button key='back' onClick={handleCancel}>
          {t('action.cancel')}
        </Button>,
        <Button
          key='submit'
          type='primary'
          onClick={handleSubmit}
          loading={updateTransportationTicketMutation.isLoading}
        >
          {t('action.submit')}
        </Button>
      ]}
    >
      <Form
        form={form}
        layout='vertical'
        fields={[
          {
            name: 'departureDate',
            value: formState.departureDate ? dayjs(formState.departureDate) : null
          },
          {
            name: 'arriveDate',
            value: formState.arriveDate ? dayjs(formState.arriveDate) : null
          },
          {
            name: 'expectedReturnDate',
            value: formState.expectedReturnDate ? dayjs(formState.expectedReturnDate) : null
          },
          {
            name: 'note',
            value: formState.note
          },
          {
            name: 'distance',
            value: formState.distance
          },
          {
            name: 'transportationTypeId',
            value: formState.transportationTypeId
          },
          {
            name: 'vehicleTypeId',
            value: formState.vehicleTypeId
          },
          {
            name: 'vehicleId',
            value: formState.vehicleId
          },
          {
            name: 'driverId',
            value: formState.driverId
          },
          {
            name: 'estimatedCost',
            value: formState.estimatedCost
          },
          {
            name: 'distance',
            value:
              formState.departureId && formState.destinationId
                ? JSON.stringify({
                    departureId: formState.departureId,
                    destinationId: formState.destinationId,
                    distance: formState.distance
                  })
                : undefined
          }
        ]}
        className='edit-transportation-form'
      >
        <Descriptions
          layout='horizontal'
          labelStyle={{ width: '135px' }}
          column={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 2 }}
        >
          <Descriptions.Item label='Số phiếu'>
            <Text strong>{transportationTicket?.code}</Text>
          </Descriptions.Item>
          {(transportationTicket?.businessTripTicketId || transportationTicket?.refTicketId) && (
            <Descriptions.Item
              label={
                transportationTicket?.businessTripTicketId
                  ? 'Số phiếu công tác'
                  : transportationTicket?.refTicketId
                  ? 'Số ticket'
                  : ''
              }
            >
              {transportationTicket?.businessTripTicketId
                ? transportationTicket?.businessTripTicket.code
                : transportationTicket?.refTicketId
                ? transportationTicket?.refTicketCode
                : ''}
            </Descriptions.Item>
          )}
          <Descriptions.Item label='Ngày tạo'>
            <Text>{formatDate(transportationTicket?.createdDate)}</Text>
          </Descriptions.Item>
          <Descriptions.Item label='Trạng thái'>
            <HRTicketStatusTag
              ticketStatus={transportationTicket?.ticketStatus}
              confirmedType={transportationTicket?.hrConfirmedType}
            />
          </Descriptions.Item>
          <Descriptions.Item label='Người tạo'>
            <Text>{transportationTicket?.createdBy?.fullName}</Text>
          </Descriptions.Item>
          <Descriptions.Item label='Loại đề nghị'>
            <Text>{transportationTicket?.requestType?.name}</Text>
          </Descriptions.Item>
          <Descriptions.Item label='Bộ phận'>
            <Text>{transportationTicket?.createdBy?.department?.name}</Text>
          </Descriptions.Item>
          <Descriptions.Item label='Mã dự án' span={2}>
            <Text>{transportationTicket?.projectCode}</Text>
          </Descriptions.Item>
          <Descriptions.Item label='Lý do đề nghị' span={2}>
            <Text strong>{transportationTicket?.note}</Text>
          </Descriptions.Item>
          <Descriptions.Item label='Lộ trình' span={colspan}>
            <Form.Item
              name='distance'
              style={{ width: '100%', marginBlockEnd: 0 }}
              rules={[
                {
                  required: true,
                  message: 'Không được để trống!'
                }
              ]}
            >
              <DistanceSelect
                onChange={handleChangeDistance}
                allowClear={false}
                placeholder='Tìm kiếm theo nơi đi, nơi đến hoặc mô tả'
              />
            </Form.Item>
          </Descriptions.Item>
          <Descriptions.Item label='Ngày đi' span={colspan}>
            <Form.Item name='departureDate' style={{ width: '100%', marginBlockEnd: 0 }}>
              <UTCNoShiftDatePicker
                className='custom-field'
                size='small'
                allowClear={false}
                onChangeUTC={(valueUTC) => setFormState((prev) => ({ ...prev, departureDate: valueUTC || undefined }))}
              />
            </Form.Item>
          </Descriptions.Item>
          <Descriptions.Item label='Ngày đến' span={colspan}>
            <Form.Item name='arriveDate' style={{ width: '100%', marginBlockEnd: 0 }}>
              {' '}
              <UTCNoShiftDatePicker
                className='custom-field'
                allowClear={false}
                onChangeUTC={(valueUTC) => valueUTC && setFormState((prev) => ({ ...prev, arriveDate: valueUTC }))}
              />
            </Form.Item>
          </Descriptions.Item>
          <Descriptions.Item label='Ngày dự kiến về' span={colspan}>
            <Form.Item name='expectedReturnDate' style={{ width: '100%', marginBlockEnd: 0 }}>
              {' '}
              <UTCNoShiftDatePicker
                className='custom-field'
                allowClear={false}
                onChangeUTC={(valueUTC) =>
                  valueUTC && setFormState((prev) => ({ ...prev, expectedReturnDate: valueUTC }))
                }
              />
            </Form.Item>
          </Descriptions.Item>
          <Descriptions.Item label='Loại di chuyển' span={colspan}>
            <Form.Item name='transportationTypeId' style={{ width: '100%', marginBlockEnd: 0 }}>
              <TransportationTypeSelect
                style={{ width: '100%' }}
                allowClear={false}
                disabled
                onChange={(value) => {
                  setFormState((prev) => ({ ...prev, transportationTypeId: value ? Number(value) : undefined }));
                }}
              />
            </Form.Item>
          </Descriptions.Item>
          <Descriptions.Item label='Chi nhánh xe' span={colspan}>
            <Form.Item name='transportationTypeId' style={{ width: '100%', marginBlockEnd: 0 }}>
              <BranchSelect value={transportationTicket?.branchId} disabled />
            </Form.Item>
          </Descriptions.Item>
          <Descriptions.Item label='Loại phương tiện' span={colspan}>
            <Form.Item name='vehicleTypeId' style={{ width: '100%', marginBlockEnd: 0 }}>
              <VehicleTypeSelect style={{ width: '100%' }} allowClear={false} onChange={handleChangeVehicleType} />
            </Form.Item>
          </Descriptions.Item>
          <Descriptions.Item label='Phương tiện' span={colspan}>
            <Form.Item name='vehicleId' style={{ width: '100%', marginBlockEnd: 0 }}>
              <VehicleSelect
                style={{ width: '100%' }}
                allowClear={false}
                vehicleTypeId={formState.vehicleTypeId}
                onChange={(value) => {
                  setFormState((prev) => ({ ...prev, vehicleId: value ? Number(value) : undefined }));
                }}
              />
            </Form.Item>
          </Descriptions.Item>
          <Descriptions.Item label='Tài xế' span={colspan}>
            <Form.Item name='driverId' style={{ width: '100%', marginBlockEnd: 0 }}>
              <DriverSelect
                style={{ width: '100%' }}
                allowClear={false}
                dateCheck={formState.departureDate}
                onChange={(value) => {
                  setFormState((prev) => ({ ...prev, driverId: value ? Number(value) : undefined }));
                }}
              />
            </Form.Item>
          </Descriptions.Item>
          <Descriptions.Item label='Nội dung vận chuyển' span={colspan}>
            <Form.Item name='note' style={{ width: '100%', marginBlockEnd: 0 }}>
              <TextArea
                className='custom-field'
                rows={1}
                onChange={(e) => setFormState((prev) => ({ ...prev, note: e.target.value }))}
              />
            </Form.Item>
          </Descriptions.Item>
          <Descriptions.Item label='Chi phí dự kiến' span={colspan}>
            <Form.Item name='estimatedCost' style={{ width: '100%', marginBlockEnd: 0 }}>
              <InputNumber
                className='custom-field'
                min={0}
                maxLength={12}
                controls={false}
                suffix={'VNĐ'}
                formatter={(value) => formatToVND(String(value))}
                onChange={(value) => setFormState((prev) => ({ ...prev, cost: value as number }))}
              />
            </Form.Item>
          </Descriptions.Item>
        </Descriptions>
      </Form>
    </Modal>
  );
}
